/**
 * Knowledge Base Page
 * Repository of articles, technical references, troubleshooting guides, best practices, and FAQs
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '@components/PageLayout';
import styles from './KnowledgeBasePage.module.css';

const KnowledgeBasePage: React.FC = () => {
  const navigate = useNavigate();

  const categories = [
    {
      id: 1,
      title: 'Best Practices',
      description: 'Advice on architecture, scaling, performance tuning, and usage patterns.',
      icon: (
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
          <path d="M2 17l10 5 10-5"></path>
          <path d="M2 12l10 5 10-5"></path>
        </svg>
      ),
      buttonText: 'View Best Practices',
      link: 'https://kb.commvault.com/',
    },
    {
      id: 2,
      title: 'Troubleshooting',
      description: 'Step-by-step resolutions for known issues and error conditions.',
      icon: (
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
        </svg>
      ),
      buttonText: 'View Troubleshooting Guides',
      link: 'https://kb.commvault.com/',
    },
    {
      id: 3,
      title: 'FAQs',
      description: 'Frequently asked questions for common tasks and scenarios.',
      icon: (
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
          <circle cx="12" cy="12" r="3"></circle>
        </svg>
      ),
      buttonText: 'View FAQs',
      link: 'https://kb.commvault.com/',
    },
  ];

  const handleActionClick = (link: string) => {
    window.open(link, '_blank');
  };

  const benefits = [
    'Faster self-resolution: Enables users to find answers independently without waiting for support, reducing downtime.',
    'Support efficiency: By deflecting common / repeat issues to the Knowledge Base, support teams can focus on more complex or novel problems.',
    'Consistency & accuracy: Ensures that recommended resolutions and best practices are documented and repeatable.',
    'Knowledge sharing & institutional memory: Helps capture organizational learning, especially across versions and environments.',
    'Onboarding & training aid: New engineers or teams can use the KB as a learning resource to get up to speed.',
  ];

  return (
    <PageLayout>
      <div className={styles.unifiedContainer}>
        {/* Knowledge Info */}
        <section className={styles.knowledgeSection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.knowledgeIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
              </svg>
            </span>
            Knowledge
          </h2>
          <p className={styles.subtitle}>The place where knowledge becomes known</p>
          <ul className={styles.introList}>
            <li>
              The Knowledge Base is a repository of articles, technical references, troubleshooting guides, best practices, and FAQs for Commvault products and solutions.
            </li>
            <li>
              It is part of Commvault’s self-help and support infrastructure so that customers, administrators, and engineers can find answers without submitting a support ticket.
            </li>
            <li>
              In many cases, it complements or is tightly integrated with the broader Documentation site (product manuals, API references, etc.).
            </li>
          </ul>
          <div className={styles.smallLine}></div>
        </section>

        {/* Category Cards Grid */}
        <div className={styles.categoryGrid}>
          {categories.map((category) => (
            <div key={category.id} className={styles.categoryCard}>
              <h3 className={styles.cardTitle}>{category.title}</h3>
              <div className={styles.cardHeaderLine}></div>
              <div className={styles.cardIconWrapper}>
                <div className={styles.squareIcon}>
                  <div className={styles.iconOuter}></div>
                  <div className={styles.iconInner}>
                    <div className={styles.cardIcon}>{category.icon}</div>
                  </div>
                </div>
              </div>
              <p className={styles.cardDescription}>{category.description}</p>
              <button 
                className={styles.cardButton}
                onClick={() => handleActionClick(category.link)}
              >
                {category.buttonText}
              </button>
            </div>
          ))}
        </div>

        {/* Value & Benefits */}
        <section className={styles.benefitsSection}>
          <h2 className={styles.benefitsTitle}>Value & Benefits</h2>
          <ul className={styles.benefitsList}>
            {benefits.map((benefit, index) => (
              <li key={index}>
                <strong>{benefit.split(':')[0]}:</strong>{' '}
                {benefit.split(':').slice(1).join(':')}
              </li>
            ))}
          </ul>
        </section>

        {/* Launch Button */}
        <div className={styles.launchSection}>
          <button
            className={styles.launchButton}
            onClick={() => handleActionClick('https://kb.commvault.com/')}
          >
            Launch Knowledge Base
          </button>
        </div>
      </div>
    </PageLayout>
  );
};

export default KnowledgeBasePage;

